import React from 'react';

// utils
import {
  SCHEMA_TYPE_BOOLEAN,
  SCHEMA_TYPE_ENUM,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_LONG,
} from '../../util/types';
import { constructQueryParamName } from '../../util/search';
import { BY_LISTING_TYPE_CATEGORY_IDS } from '../../config/r2rListingTypeCategories';
import { buildSubcategoryFilterOptions } from './SearchPage.shared';

// The URL parser (urlHelpers.js) converts 'true'/'false' strings to booleans.
// SelectMultipleFilter expects string option values, so coerce them back.
const boolToStr = obj =>
  obj
    ? Object.fromEntries(
        Object.entries(obj).map(([k, v]) => [k, typeof v === 'boolean' ? String(v) : v])
      )
    : obj;

// component imports
import SelectSingleFilter from './SelectSingleFilter/SelectSingleFilter';
import SelectMultipleFilter from './SelectMultipleFilter/SelectMultipleFilter';
import BookingDateRangeFilter from './BookingDateRangeFilter/BookingDateRangeFilter';
import KeywordFilter from './KeywordFilter/KeywordFilter';
import PriceFilter from './PriceFilter/PriceFilter';
import IntegerRangeFilter from './IntegerRangeFilter/IntegerRangeFilter';
import SeatsFilter from './SeatsFilter/SeatsFilter';

/**
 * FilterComponent is used to map configured filter types
 * to actual filter components
 */
const FilterComponent = props => {
  const {
    config,
    urlQueryParams,
    initialValues,
    getHandleChangedValueFn,
    listingCategories,
    marketplaceCurrency,
    intl,
    ...rest
  } = props;
  // Note: config can be either
  // - listingFields config or
  // - default filter config
  // They both have 'key' and 'schemaType' included.
  const { key, schemaType } = config;
  const { liveEdit, showAsPopup } = rest;

  const useHistoryPush = liveEdit || showAsPopup;
  const name = key.replace(/\s+/g, '-');
  const getAriaLabel = (label, values) => {
    const status = values ? 'active' : 'inactive';
    const mode = liveEdit ? 'live' : 'normal';
    return intl.formatMessage(
      { id: 'SearchPage.screenreader.openFilterButton' },
      { label, status, values, mode }
    );
  };

  // Default filters: price, keywords, dates
  switch (schemaType) {
    case 'category': {
      const { scope, nestedParams } = config;
      const level1Param = constructQueryParamName(nestedParams?.[0] || 'categoryLevel1', scope);
      const level2Param = constructQueryParamName(nestedParams?.[1] || 'categoryLevel2', scope);
      const label = intl.formatMessage({ id: 'FilterComponent.categoryLabel' });
      const subcategoryLabel = intl.formatMessage({ id: 'FilterComponent.subcategoryLabel' });

      // Determine which listing types are currently selected from URL params
      const listingTypeParam = constructQueryParamName('listingType', scope);
      const selectedListingTypeRaw = urlQueryParams?.[listingTypeParam];
      const selectedListingTypes = selectedListingTypeRaw
        ? selectedListingTypeRaw.split(',').filter(Boolean)
        : [];

      // If listing types are selected, show only their allowed top-level categories.
      // Fall back to null (show all) if none of the selected types are in our map,
      // so unrecognised listing types never produce an empty category list.
      const rawAllowedIds =
        selectedListingTypes.length > 0
          ? [...new Set(selectedListingTypes.flatMap(lt => BY_LISTING_TYPE_CATEGORY_IDS[lt] || []))]
          : null;
      const allowedCategoryIds = rawAllowedIds?.length > 0 ? rawAllowedIds : null;

      const topLevelOptions = (listingCategories || [])
        .filter(cat => !allowedCategoryIds || allowedCategoryIds.includes(cat.id))
        .map(cat => ({ option: cat.id, label: cat.name }));

      // Subcategory options = union of children under currently selected top-level categories.
      const selectedCategoryRaw = urlQueryParams?.[level1Param];
      const subcategoryOptions = buildSubcategoryFilterOptions(
        listingCategories,
        selectedCategoryRaw
      );

      // When top-level category changes, clear any stale subcategory params
      const handleCategorySubmit = values => {
        getHandleChangedValueFn(useHistoryPush)({ ...values, [level2Param]: null });
      };

      const { id: filterId, ...restWithoutId } = rest;

      return (
        <>
          <SelectMultipleFilter
            id={filterId}
            name={name}
            label={label}
            queryParamNames={[level1Param]}
            initialValues={initialValues([level1Param], liveEdit)}
            onSubmit={handleCategorySubmit}
            options={topLevelOptions}
            schemaType={SCHEMA_TYPE_ENUM}
            getAriaLabel={getAriaLabel}
            scrollListMode={topLevelOptions.length > 0}
            scrollLegendId="SelectMultipleFilter.categoryScrollHint"
            {...restWithoutId}
          />
          {subcategoryOptions.length > 0 ? (
            <SelectMultipleFilter
              id={filterId ? `${filterId}.subcategory` : undefined}
              name={`${name}-subcategory`}
              label={subcategoryLabel}
              queryParamNames={[level2Param]}
              initialValues={initialValues([level2Param], liveEdit)}
              onSubmit={getHandleChangedValueFn(useHistoryPush)}
              options={subcategoryOptions}
              schemaType={SCHEMA_TYPE_ENUM}
              getAriaLabel={getAriaLabel}
              scrollListMode={subcategoryOptions.length > 0}
              scrollLegendId="SelectMultipleFilter.categoryScrollHint"
              {...restWithoutId}
            />
          ) : null}
        </>
      );
    }
    case 'listingType': {
      const { scope, options } = config;
      const paramNames = [constructQueryParamName(key, scope)];
      const label = intl.formatMessage({ id: 'FilterComponent.listingTypeLabel' });

      const categoryLevel1Param = constructQueryParamName('categoryLevel1', scope);
      const categoryLevel2Param = constructQueryParamName('categoryLevel2', scope);

      // When listing type changes, clear category selections to avoid stale/invalid combos
      const handleListingTypeSubmit = values => {
        getHandleChangedValueFn(useHistoryPush)({
          ...values,
          [categoryLevel1Param]: null,
          [categoryLevel2Param]: null,
        });
      };

      return (
        <SelectMultipleFilter
          name={name}
          label={label}
          queryParamNames={paramNames}
          initialValues={initialValues(paramNames, liveEdit)}
          onSubmit={handleListingTypeSubmit}
          options={options}
          schemaType={SCHEMA_TYPE_ENUM}
          getAriaLabel={getAriaLabel}
          // Listing-type option icons are omitted for now. Restore with ListingTypeIcon + iconForOption.
          {...rest}
        />
      );
    }
    case 'price': {
      const { min, max, step } = config;
      return (
        <PriceFilter
          name={key}
          label={intl.formatMessage({ id: 'FilterComponent.priceLabel' })}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          min={min}
          max={max}
          step={step}
          marketplaceCurrency={marketplaceCurrency}
          getAriaLabel={getAriaLabel}
          {...rest}
        />
      );
    }
    case 'keywords':
      const label = intl.formatMessage({ id: 'FilterComponent.keywordsLabel' });

      return (
        <KeywordFilter
          label={label}
          name={name}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          getAriaLabel={getAriaLabel}
          {...rest}
        />
      );
    case 'dates': {
      const { dateRangeMode } = config;
      const isNightlyMode = dateRangeMode === 'night';
      return (
        <BookingDateRangeFilter
          label={intl.formatMessage({ id: 'FilterComponent.datesLabel' })}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          minimumNights={isNightlyMode ? 1 : 0}
          getAriaLabel={getAriaLabel}
          {...rest}
        />
      );
    }
    case 'seats': {
      const label = intl.formatMessage({ id: 'FilterComponent.seatsLabel' });
      return (
        <SeatsFilter
          name={name}
          label={label}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          getAriaLabel={getAriaLabel}
          {...rest}
        />
      );
    }
  }

  // Custom extended data filters
  switch (schemaType) {
    case SCHEMA_TYPE_BOOLEAN: {
      // Boolean listing-field filter: renders a single checkbox that sets param=true.
      // Used when a Console listing field has schemaType 'boolean' and filterConfig.enabled = true.
      const { scope, filterConfig = {} } = config;
      const { label } = filterConfig;
      const queryParamNames = [constructQueryParamName(key, scope)];
      return (
        <SelectMultipleFilter
          label={label}
          getAriaLabel={(lbl, vals) => `${lbl} filter`}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={boolToStr(initialValues(queryParamNames, liveEdit))}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={[{ option: 'true', label }]}
          schemaType={SCHEMA_TYPE_ENUM}
          {...rest}
        />
      );
    }
    case SCHEMA_TYPE_ENUM: {
      const { scope, enumOptions, filterConfig = {} } = config;
      const { label, filterType } = filterConfig;
      const queryParamNames = [constructQueryParamName(key, scope)];
      return filterType === 'SelectSingleFilter' ? (
        <SelectSingleFilter
          label={label}
          getAriaLabel={getAriaLabel}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={enumOptions}
          isNestedEnum={false}
          {...rest}
        />
      ) : (
        <SelectMultipleFilter
          label={label}
          getAriaLabel={getAriaLabel}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={enumOptions}
          schemaType={schemaType}
          {...rest}
        />
      );
    }
    case SCHEMA_TYPE_MULTI_ENUM: {
      const { scope, enumOptions, filterConfig = {} } = config;
      const { label, searchMode } = filterConfig;
      const queryParamNames = [constructQueryParamName(key, scope)];
      return (
        <SelectMultipleFilter
          label={label}
          getAriaLabel={getAriaLabel}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={enumOptions}
          schemaType={schemaType}
          searchMode={searchMode}
          {...rest}
        />
      );
    }
    case SCHEMA_TYPE_LONG: {
      const { minimum, maximum, scope, step, filterConfig = {} } = config;
      const { label } = filterConfig;
      const queryParamNames = [constructQueryParamName(key, scope)];
      return (
        <IntegerRangeFilter
          label={label}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          min={minimum}
          max={maximum}
          step={step}
          getAriaLabel={getAriaLabel}
          {...rest}
        />
      );
    }
    default:
      return null;
  }
};

export default FilterComponent;
